/**
 * TanStack Query hooks — Contacts (nested sous une company).
 *
 * Routes API :
 *   GET  /v1/companies/:companyId/contacts
 *   POST /v1/companies/:companyId/contacts
 *
 * 8.B — type lecture importé depuis @kollectivia/shared.
 */

import type { ContactRow, DbPage } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

export type Contact = ContactRow;
export type ContactListResult = DbPage<ContactRow>;

export type ContactListParams = {
  cursor?: string | null;
  limit?: number;
  q?: string;
  is_primary?: boolean;
};

function buildContactsPath(companyId: string, params: ContactListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.q) sp.set("q", params.q);
  if (params.is_primary !== undefined) sp.set("is_primary", String(params.is_primary));
  const qs = sp.toString();
  const base = `/v1/companies/${companyId}/contacts`;
  return qs ? `${base}?${qs}` : base;
}

export function useContacts(companyId: string | undefined, params: ContactListParams = {}) {
  return useQuery({
    queryKey: ["contacts", "list", companyId, params],
    queryFn: () => api.get<ContactListResult>(buildContactsPath(companyId!, params)),
    enabled: !!companyId,
    placeholderData: (prev, previousQuery) =>
      previousQuery?.queryKey[2] === companyId ? prev : undefined,
  });
}

export function useCreateContact(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<ContactRow>(`/v1/companies/${companyId}/contacts`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts", "list", companyId] });
    },
  });
}

/** Reuse the existing contact command; never create a duplicate to add an email. */
export function useUpdateContact(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { full_name: string; email: string } }) =>
      api.patch<ContactRow>(`/v1/contacts/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts", "list", companyId] });
    },
  });
}
